import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
    ArrowCircleUpRightIcon,
    ArrowRightIcon,
    CaretUpDownIcon,
    QuestionIcon,
    RocketLaunchIcon,
    UploadSimpleIcon,
} from '@phosphor-icons/react'
import ScoopLogo from '../../assets/common/ScoopLogo.svg'
import { navbarItems } from './navbarItems'
import NavbarItem from './NavBarItem/NavBarItem'
import styles from './NavBar.module.scss'
import { switchWorkspace } from '../../store/actions/authActions'
import { ROUTES } from '../../router/routes'
import { useApi } from '../../api/api'
import {
    toggleSwitchWorkspaceModal,
    canShowTourAlert,
    setResetTrigger,
} from '../../store/actions/uiActions'
import Welcome from '../../assets/account/Welcome.svg'
import { useWorkspaces } from '../../api/hooks/workspaces/useWorkspaces'
import { SwitchWorkspacePopover } from '../SwitchWorkspacePopover/SwitchWorkspacePopover'
import { getLastUsedWorkspaces, getWorkspaceID } from '../../utils'
import { useUserInfo } from '../../api/hooks/user/useUserInfo'
import { AccountDropdown } from '../AccountDropdown/AccountDropdown'
import { useWorkspaceMetadata } from '../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import {
    ScoopAvatar,
    ScoopButton,
    ScoopDialog,
    ScoopLinearProgress,
    ScoopText,
    ScoopTooltip,
    ScoopRow,
    ScoopChip,
} from '../ui'
import TopBanner from './components/TopBanner/TopBanner'
import { useProcessingPollingContext } from '../../context/ProcessingPollingContext'
import {
    POLLING_CHECK_ACTIONS,
    useProcessingPolling,
} from '../../api/hooks/processing/useProcessingPolling'
import { useSystemMessages } from '../../context/SystemMessagesContext'
import { EVENTS, trackEvent } from '../../events'
import { useIsTourWorkspace } from '../../hooks/useIsTourWorkspace'
import { OnboardingChecklist } from './components/OnboardingChecklist/OnboardingChecklist'
import { useOnboarding } from '../../api/hooks/onboarding/useOnboarding'
import { ONBOARDING_GRACE_PERIOD_DAYS } from '../../api/hooks/onboarding/onboardingApi'

// these are hardcoded defaults for when there's a new user and we need to kick off the tour
export const DEFAULT_WORKSPACE_NAME = 'Tour Scoop'
export const OLD_DEFAULT_WORKSPACE_NAME = 'Tour Scoop Workspace'

// Admin-only routes that show the admin indicator
const ADMIN_ROUTES = [
    ROUTES.EXPLORERV2,
    ROUTES.LIVE_QUERY,
    ROUTES.SCOOPADMIN,
    ROUTES.DOMAIN_INTELLIGENCE,
]

const ALLOWED_TOUR_TABS = ['Insights']

const NavBar = ({ workspaceName }) => {
    const isTourWorkspace = useIsTourWorkspace()
    const [anchorEl, setAnchorEl] = useState(null)
    const switchAnchorRef = useRef(null)
    const [alertOpen, setAlertOpen] = useState(false)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const defaultTourWorkspace = useSelector((state) => state.auth.defaultTourWorkspace)
    const userID = useSelector((state) => state.auth.userID)
    const userName = useSelector((state) => state.auth.userName)
    const readyToShowAlert = useSelector((state) => state.ui.canShowTourAlert)
    const { canvasID } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const isMenuOpen = Boolean(anchorEl)
    const [showUserProfile, setShowUserProfile] = useState(false)
    const { postData: postDataDefault } = useApi()
    const isGuestMode = useSelector((state) => state.auth.isGuestMode)
    const isWorkspacePopoverOpen = useSelector((state) => state.ui.switchWorkspaceModalOpen)
    const isPolling = useSelector((state) => state.ui.isCanvasPolling)
    const [openWorkspaceActionsModal, setOpenWorkspaceActionsModal] = useState(false)
    const [openCanvasProgressModal, setOpenCanvasProgressModal] = useState(false)
    const [mockedProgress, setMockedProgress] = useState(0)

    const inboxID = localStorage.getItem('generateCanvasInboxID')

    // Onboarding state
    const { isFeatureUnlocked, isOnboardingComplete, isStepCompleted, STEPS } = useOnboarding()

    const { workspaces: workspacesAccessible } = useWorkspaces()
    const { user } = useUserInfo()

    // Processing polling logic
    const { pollingData, stopPolling } = useProcessingPollingContext()
    const [isProcessingPolling, setIsProcessingPolling] = useState(false)
    const { showSuccess, showError, showInfo, clearMessage } = useSystemMessages()

    // Start polling when we have polling data
    useEffect(() => {
        if (pollingData?.inbox) {
            setIsProcessingPolling(true)
        }
    }, [pollingData])

    const changeWorkspace = () => {
        if (!pollingData) return
        dispatch(switchWorkspace(pollingData.workspaceID, pollingData.workspaceName))
        dispatch(setResetTrigger())
        navigate('/sources')
        clearMessage()
        stopPolling()
    }

    // Use the polling hook
    useProcessingPolling(
        POLLING_CHECK_ACTIONS.PROCESSING,
        isProcessingPolling,
        setIsProcessingPolling,
        pollingData?.inbox,
        (data) => {
            if (data?.error === 'Request timed out') {
                trackEvent(EVENTS.ONBOARDING.DATA_UPLOAD_FAILED)
                // Show error message when max polling is reached
                showError('We could not process your data in time. Please contact support.', {
                    dismissible: true,
                })
            } else {
                // Show success message when processing completes
                showSuccess('Your data has been processed successfully!', {
                    dismissible: true,
                    rightSide: (
                        <ScoopRow onClick={changeWorkspace} className={styles.successMessageRow}>
                            <ScoopText className={styles.successMessageText}>
                                {pollingData?.workspaceName
                                    ? `Go to ${pollingData.workspaceName}`
                                    : 'Go to workspace'}
                            </ScoopText>
                            <ArrowCircleUpRightIcon size={16} weight="bold" color="#38823b" />
                        </ScoopRow>
                    ),
                })
                // Navigate to chat with flag to show onboarding prompts if onboarding is not complete
                if (!isOnboardingComplete()) {
                    navigate(ROUTES.INSIGHTS, {
                        state: {
                            showOnboardingPrompts: true,
                            createdInbox: pollingData?.inbox,
                        },
                    })
                }
            }
            // Clear polling data after completion
            stopPolling()
            setIsProcessingPolling(false)
        },
        300, // Max polling attempts (300 * 2 seconds = 10 minutes)
        null, // No middle polling callback
        pollingData?.workspaceID // Pass the workspace ID from polling data
    )
    const { workspaceMetadata } = useWorkspaceMetadata()

    // Check if current route is an admin-only route
    const isAdminRoute = ADMIN_ROUTES.some((route) => location.pathname.startsWith(route))

    // Feature gating for onboarding - map routes to features
    const getFeatureForRoute = (route) => {
        if (route === ROUTES.EXPLORER || route === ROUTES.EXPLORERV2) return 'explorer'
        if (route === ROUTES.CANVAS_DASHBOARD) return 'canvas'
        if (route === ROUTES.SOURCES) return 'sources'
        if (route === ROUTES.LIVE_WORKSHEETS) return 'liveWorksheets'
        return 'chat' // Default to chat (always unlocked)
    }

    // Account longevity check - users with accounts older than grace period don't need onboarding
    const accountAgeDays = user?.createDate
        ? (Date.now() - user.createDate) / (1000 * 60 * 60 * 24)
        : 0
    const isExistingUser = accountAgeDays > ONBOARDING_GRACE_PERIOD_DAYS

    const isRouteLockedForOnboarding = (route) => {
        // Skip onboarding checks if onboarding is complete (localStorage)
        if (isOnboardingComplete()) return false

        // Skip onboarding checks for existing users (account > 7 days old)
        if (isExistingUser) return false

        const feature = getFeatureForRoute(route)

        // For Tour Scoop, only apply onboarding gating to Explorer
        // (Canvas and Sources are already locked by ALLOWED_TOUR_TABS)
        if (isTourWorkspace) {
            if (feature === 'explorer') {
                return !isFeatureUnlocked(feature)
            }
            return false // Let ALLOWED_TOUR_TABS handle other routes
        }

        return !isFeatureUnlocked(feature)
    }

    const getOnboardingTooltip = (route) => {
        const feature = getFeatureForRoute(route)
        if (feature === 'explorer') return 'Click "Open in Explorer" on a chart to unlock'
        if (feature === 'canvas') return 'Click "Open in Explorer" on a chart to unlock Canvas'
        if (feature === 'sources') return 'Complete onboarding to unlock Sources'
        if (feature === 'liveWorksheets') return 'Complete onboarding to unlock Live Worksheets'
        return ''
    }

    // Show Tour banner when in Tour workspace and step 3 (Connect Data) is not complete
    // Don't show when already on the onboarding page
    useEffect(() => {
        const step3Complete = isStepCompleted(STEPS.SAVE_TO_CANVAS)
        const isOnOnboardingPage = location.pathname === ROUTES.ONBOARDING

        if (isTourWorkspace && !step3Complete && !isExistingUser && !isOnOnboardingPage) {
            showInfo(
                <span className={styles.bannerMessage}>
                    <UploadSimpleIcon size={18} weight="bold" className={styles.bannerIcon} />
                    <span className={styles.bannerText}>Want to see Scoop with your own data?</span>
                    <button
                        className={styles.bannerButton}
                        onClick={() => navigate(ROUTES.ONBOARDING, { state: { stepIndex: 1 } })}
                    >
                        Connect now
                    </button>
                </span>,
                { dismissible: false }
            )
        } else if (!isTourWorkspace || step3Complete || isOnOnboardingPage) {
            // Clear the banner when leaving Tour workspace, completing step 3, or on onboarding page
            clearMessage()
        }
    }, [
        isTourWorkspace,
        isStepCompleted,
        STEPS,
        isExistingUser,
        showInfo,
        clearMessage,
        navigate,
        location.pathname,
    ])

    useEffect(() => {
        if (!isPolling) return

        const start = Date.now()
        const duration = 310_000

        const interval = setInterval(() => {
            const elapsed = Date.now() - start
            const percentage = Math.min((elapsed / duration) * 99, 99.9)

            if (percentage >= 99) {
                clearInterval(interval)
            }

            setMockedProgress(percentage)
        }, 1000)

        return () => clearInterval(interval)
    }, [isPolling])

    useEffect(() => {
        let currentWorkspace = workspacesAccessible.find(
            (workspace) => workspace.workspaceID === workspaceID
        )

        if (!readyToShowAlert) return
        if (currentWorkspace) {
            if (defaultTourWorkspace) {
                if (
                    defaultTourWorkspace === currentWorkspace.workspaceID ||
                    currentWorkspace.name === DEFAULT_WORKSPACE_NAME
                ) {
                    setAlertOpen(true)
                } else {
                    alertOpen && setAlertOpen(false)
                    readyToShowAlert && dispatch(canShowTourAlert(false))
                }
            } else {
                if (currentWorkspace.name === DEFAULT_WORKSPACE_NAME) {
                    setAlertOpen(true)
                } else {
                    alertOpen && setAlertOpen(false)
                    readyToShowAlert && dispatch(canShowTourAlert(false))
                }
            }
        } else setAlertOpen(false)
    }, [workspacesAccessible, workspaceID, defaultTourWorkspace, readyToShowAlert])

    useEffect(() => {
        const redirect = localStorage.getItem('redirectUrl')
        if (redirect) {
            navigate(redirect)
            localStorage.removeItem('redirectUrl')
        }
    }, [])

    // If not detault workspace has been selected (i.e. new user)
    useEffect(() => {
        let lastUsedWorkspaces = getLastUsedWorkspaces()
        const lastUsed = lastUsedWorkspaces.get(userID)
        if (lastUsed && !getWorkspaceID()) {
            dispatch(switchWorkspace(lastUsed[0], lastUsed[1]))
        } else {
            if (workspacesAccessible.length > 0 && !getWorkspaceID()) {
                const tour = workspacesAccessible.find(
                    (w) => w.workspaceID === defaultTourWorkspace
                )
                const tourWorkspace = workspacesAccessible.find(
                    (workspace) =>
                        workspace.name === DEFAULT_WORKSPACE_NAME ||
                        workspace.name === OLD_DEFAULT_WORKSPACE_NAME
                )
                if (tour) dispatch(switchWorkspace(tour.workspaceID, tour.name))
                else dispatch(switchWorkspace(tourWorkspace?.workspaceID, tourWorkspace?.name))
                dispatch(setResetTrigger())
                navigate(ROUTES.INSIGHTS)
            }
        }
    }, [workspacesAccessible])

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const RenderTooltip = ({ children }) => {
        return <ScoopTooltip title={'Switch Workspace'}>{children}</ScoopTooltip>
    }

    const WorkspaceExplainer = () => {
        return (
            <div className={styles.workspaceExplainer}>
                <div className={styles.explainerImageContainer}>
                    <img alt={'image'} src={Welcome} />
                </div>
                <div className={styles.explainerContent}>
                    <div className={styles.explainerTextContainer}>
                        <ScoopText className={styles.explainerTitle}>Your Demo Workspace</ScoopText>
                        <ScoopText className={styles.explainerDescription}>
                            Here you can explore Scoop with sample data and see example canvases.
                            Click the Workspace switcher to get back to your Workspace and use your
                            own data.
                        </ScoopText>
                    </div>
                    <div className={styles.explainerButtonContainer}>
                        <ScoopButton
                            fullWidth
                            variant="primary"
                            size="small"
                            onClick={() => setAlertOpen(false)}
                        >
                            Got it
                        </ScoopButton>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            {!isGuestMode && (
                <div className={styles.appBar}>
                    <div className={`${styles.toolbar}`}>
                        <div className={styles.scoopLogoContainer}>
                            <img
                                src={ScoopLogo}
                                alt="Scoop Logo"
                                onClick={() => navigate(ROUTES.INSIGHTS)}
                            />
                        </div>
                        <div className={styles.navbarItemsContainer}>
                            <div
                                name={'switch-workspace-button'}
                                ref={switchAnchorRef}
                                onClick={() =>
                                    dispatch(toggleSwitchWorkspaceModal(!isWorkspacePopoverOpen))
                                }
                                className={`${styles.navbarItem} ${styles.switchWorkspaceButton}`}
                            >
                                <RenderTooltip>
                                    <div className={styles.switchWorkspaceContent}>
                                        <ScoopText size="md" className={styles.workspaceName}>
                                            {workspaceName}
                                        </ScoopText>
                                        <CaretUpDownIcon size={16} color="white" weight="regular" />
                                    </div>
                                </RenderTooltip>
                            </div>
                            {navbarItems
                                .filter((item) => {
                                    // Hide Live Worksheets for appsumo accounts
                                    if (
                                        (workspaceMetadata?.account?.accountType?.startsWith(
                                            'appsumo'
                                        ) ||
                                            workspaceName === 'Tour Scoop') &&
                                        item.label === 'Live Worksheets'
                                    ) {
                                        return false
                                    }
                                    // Hide admin-only items for non-A1 accounts
                                    if (item.adminOnly && user?.accountID !== 'A1') {
                                        return false
                                    }
                                    return true
                                })
                                .map((item, index) => {
                                    // In Tour mode, Explorer is unlocked via onboarding (after step 1)
                                    // Other tabs remain locked by ALLOWED_TOUR_TABS
                                    const isExplorerInTour =
                                        isTourWorkspace && item.label === 'Explorer'
                                    const isLockedForTour = isExplorerInTour
                                        ? false // Explorer lock is handled by onboarding gating
                                        : isTourWorkspace && !ALLOWED_TOUR_TABS.includes(item.label)
                                    const isLockedForOnboarding = isRouteLockedForOnboarding(
                                        item.route
                                    )
                                    const isDisabled = isLockedForTour || isLockedForOnboarding

                                    // Show tooltip for onboarding-locked items, or generic tooltip for tour-locked items
                                    let disabledTooltip
                                    if (isLockedForOnboarding) {
                                        disabledTooltip = getOnboardingTooltip(item.route)
                                    } else if (isLockedForTour) {
                                        disabledTooltip = 'Not available in Tour mode'
                                    }

                                    return (
                                        <NavbarItem
                                            key={index}
                                            icon={item.icon}
                                            label={item.label}
                                            route={item.route}
                                            selected={location.pathname
                                                .split('/')
                                                .some((s) => s === item.route.replace(/\//g, ''))}
                                            onClick={() => navigate(item.route)}
                                            disabled={isDisabled}
                                            disabledTooltip={disabledTooltip}
                                        />
                                    )
                                })}
                        </div>
                        {isAdminRoute ? (
                            <ScoopChip size="medium" className={styles.adminChip}>
                                ADMIN
                            </ScoopChip>
                        ) : (
                            <div style={{ flexGrow: 1 }} />
                        )}
                        <div className={styles.rightSection}>
                            {isPolling && (
                                <div
                                    className={styles.checklistButton}
                                    onClick={() => setOpenCanvasProgressModal(true)}
                                >
                                    <RocketLaunchIcon size={16} weight="fill" color="white" />
                                    <ScoopText className={styles.checklistText}>
                                        {`Progress: ${mockedProgress.toFixed(0)}%`}
                                    </ScoopText>
                                </div>
                            )}
                            <OnboardingChecklist isTourWorkspace={isTourWorkspace} />
                            <TopBanner />
                            <a
                                href="https://www.scoopanalytics.com/help"
                                target="_blank"
                                rel="noreferrer"
                                className={styles.helpLink}
                                onClick={() =>
                                    trackEvent(EVENTS.ACCOUNT.CONTACT_SUPPORT, {
                                        source: 'navbar_help',
                                    })
                                }
                            >
                                <div
                                    aria-label="show new notifications"
                                    className={styles.navbarItem}
                                >
                                    <QuestionIcon size={24} color="white" weight="regular" />
                                </div>
                            </a>
                            <div
                                className={styles.navbarItem}
                                aria-label="account of current user"
                                aria-haspopup="true"
                                onClick={handleProfileMenuOpen}
                            >
                                <ScoopAvatar
                                    firstName={user?.firstName}
                                    lastName={user?.lastName}
                                    size="sm"
                                    onClick={handleProfileMenuOpen}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <AccountDropdown
                anchorEl={anchorEl}
                isMenuOpen={isMenuOpen}
                setAnchorEl={setAnchorEl}
                workspaceName={workspaceName}
            />
            <SwitchWorkspacePopover
                open={isWorkspacePopoverOpen}
                anchorEl={switchAnchorRef.current}
                onClose={() => dispatch(toggleSwitchWorkspaceModal(false))}
            />
            {openCanvasProgressModal && (
                <ScoopDialog
                    open={openCanvasProgressModal}
                    onClose={() => setOpenCanvasProgressModal(false)}
                    title={
                        <div className={styles.progressModalTitle}>
                            <div className={styles.rocketIconContainer}>
                                <RocketLaunchIcon size={16} weight="fill" color="#E6004C" />
                            </div>
                            <ScoopText className={styles.modalTitleText}>Progess status</ScoopText>
                        </div>
                    }
                    style={{ width: '477px', height: 'auto' }}
                >
                    <div className={styles.progressModalContent}>
                        <div className={styles.canvasTitleContainer}>
                            <ScoopText className={styles.canvasTitle}>
                                AI Presentation Canvas{' '}
                            </ScoopText>
                            <ArrowRightIcon
                                size={16}
                                weight="regular"
                                color="#262626"
                                className={styles.arrowIcon}
                                onClick={() => {
                                    setOpenCanvasProgressModal(false)
                                    navigate(ROUTES.CANVAS_DASHBOARD)
                                }}
                            />
                        </div>
                        <ScoopText className={styles.progressStatusText}>
                            Canvas Generating...
                        </ScoopText>
                        <div className={styles.progressBarSection}>
                            <ScoopLinearProgress
                                variant="determinate"
                                value={mockedProgress}
                                className={styles.modalProgressBar}
                            />
                            <ScoopText className={styles.progressPercentage}>
                                {`${mockedProgress.toFixed(0)}%`}
                            </ScoopText>
                        </div>
                    </div>
                </ScoopDialog>
            )}
            {alertOpen && <WorkspaceExplainer />}
        </>
    )
}

export default NavBar
